import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faFolder, faFolderPlus, faPlus, faSearch, faThumbtack, faTrash } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { isSubfoldersEnabled } from '../utils/folderHierarchy';
import DataTable, { createActionColumn } from './DataTable';
import './Pages.css';
import './Sidebar.css'; // Import sidebar styles for dropdown consistency

/**
 * FoldersPage component for managing campaign folders
 * Displays all folders for the current account with basic CRUD operations
 */
const FoldersPage = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData()); // Get user data once on mount
  const accountId = getAccountIdToUse();
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  // One-level nesting (e.g. Market -> Advertiser), per-account opt-in
  const subfoldersEnabled = isSubfoldersEnabled(accountSettings);

  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});

  // Helper function to get persisted pagination state
  const getPersistedPaginationState = () => {
    try {
      const saved = sessionStorage.getItem('foldersPage_paginationState');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('🔄 Restored folders pagination state:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error loading folders pagination state:', error);
    }
    return {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: ''
    };
  };

  // Initialize pagination state from sessionStorage
  const initialState = getPersistedPaginationState();

  // New state for filtering, sorting, and pagination
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  // Search-as-you-type against GET /api/v2/folders — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final
  // term. The raw searchTerm still persists to sessionStorage instantly.
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [pagination, setPagination] = useState({
    currentPage: initialState.currentPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: initialState.itemsPerPage,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Create folder inline state
  const [showCreateRow, setShowCreateRow] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Top-level folder the new folder will be created under (null = top level)
  const [createParent, setCreateParent] = useState(null);

  // Edit folder inline state
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Move-folder modal state (sub-folders feature)
  const [movingFolder, setMovingFolder] = useState(null);
  const [moveTargets, setMoveTargets] = useState([]);
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);
  const [selectedMoveTarget, setSelectedMoveTarget] = useState(null); // folder object, or 'top' for top level
  const [isMovingFolder, setIsMovingFolder] = useState(false);
  const [moveError, setMoveError] = useState(null);

  // Helper function to persist pagination state
  const persistPaginationState = (state) => {
    try {
      sessionStorage.setItem('foldersPage_paginationState', JSON.stringify(state));
      console.log('💾 Saved folders pagination state:', state);
    } catch (error) {
      console.error('Error saving folders pagination state:', error);
    }
  };

  // Persist pagination state whenever it changes
  useEffect(() => {
    const state = {
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm
    };
    persistPaginationState(state);
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    if (accountId) {
      fetchFolders();
    }
  }, [accountId, debouncedSearchTerm, sortBy, sortOrder, currentPage, itemsPerPage]);

  const fetchFolders = async () => {
    if (!accountId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: debouncedSearchTerm,
          sortBy,
          sortOrder,
          page: currentPage,
          limit: itemsPerPage
        }
      });

      if (response.status === 200) {
        const foldersData = response.data.folders || response.data;

        // Page-overflow recovery, same as CampaignsPage. A currentPage past
        // the end of the result returns an empty page while totalItems says
        // there are rows — "No folders yet" under a count badge reading 5.
        // Switching accounts is the usual way in (handled at the source now,
        // in AccountContext.selectAccount), but a session that already holds a
        // stale page, or one where enough folders were deleted to retire the
        // last page, still needs to recover itself.
        const pg = response.data.pagination;
        if (pg && pg.totalItems > 0 && currentPage > (pg.totalPages || 1) && currentPage !== 1) {
          setCurrentPage(1);
          return; // the effect re-fires fetchFolders with page=1
        }

        setFolders(foldersData);

        // Use backend pagination data if available
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          // Fallback for old API format
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: foldersData.length,
            itemsPerPage: foldersData.length,
            hasNextPage: false,
            hasPrevPage: false
          });
        }
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      const errorMessage = err.response?.data?.error || 'Failed to load folders';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    // Show confirmation dialog
    const campaignCount = folder.campaign_count || folder.campaignCount || 0;
    const confirmMessage = campaignCount > 0
      ? `Are you sure you want to delete "${folder.name}"? This folder contains ${campaignCount} campaign${campaignCount > 1 ? 's' : ''} and cannot be deleted.`
      : `Are you sure you want to delete the folder "${folder.name}"? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // If folder has campaigns, show error and return
    if (campaignCount > 0) {
      setError('Cannot delete folder that contains campaigns. Please move or delete the campaigns first.');
      return;
    }

    try {
      setError(null);

      await api.delete(getApiUrl(`/api/v2/folders/${folder.id}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });

      // Remove folder from local state
      setFolders(prev => prev.filter(f => f.id !== folder.id));

      // Update pagination totalItems count
      setPagination(prev => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1)
      }));

      // Close dropdown only on successful completion
      setOpenDropdownId(null);

    } catch (err) {
      console.error('Error deleting folder:', err);
      const errorMessage = err.response?.data?.error || 'Failed to delete folder';
      setError(errorMessage);
      // Keep dropdown open on error so user can retry
    }
  };

  const handlePinFolder = async (folder) => {
    setError(null);

    try {
      const response = await api.patch(getApiUrl(`/api/v2/folders/${folder.id}`), {
        is_pinned: !folder.is_pinned
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        setFolders(prev => {
          // Update the folder's pin status
          const updatedFolders = prev.map(f =>
            f.id === folder.id ? { ...f, is_pinned: !f.is_pinned } : f
          );

          // Sort folders with pinned items first, then by current sort criteria
          return updatedFolders.sort((a, b) => {
            // Pinned folders first
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;

            // Within same pin status, sort by current sort criteria
            let comparison = 0;

            if (sortBy === 'name') {
              comparison = a.name.localeCompare(b.name);
            } else if (sortBy === 'campaign_count') {
              comparison = (a.campaign_count || 0) - (b.campaign_count || 0);
            } else if (sortBy === 'created_at') {
              comparison = new Date(a.created_at) - new Date(b.created_at);
            } else if (sortBy === 'updated_at') {
              comparison = new Date(a.updated_at) - new Date(b.updated_at);
            } else if (sortBy === 'is_pinned') {
              comparison = (a.is_pinned ? 1 : 0) - (b.is_pinned ? 1 : 0);
            }

            // Apply sort order (asc/desc)
            return sortOrder === 'asc' ? comparison : -comparison;
          });
        });
        // Close dropdown only on successful completion
        setOpenDropdownId(null);
      }
    } catch (error) {
      console.error('Error updating folder pin status:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update folder';
      setError(errorMessage);
      // Keep dropdown open on error so user can retry
    }
  };

  // Handle search with debouncing (the fetch keys off `debouncedSearchTerm`;
  // this just updates the input and resets to page 1).
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'desc';
    if (sortBy === key && sortOrder === 'desc') {
      direction = 'asc';
    }
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortBy !== key) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      setIsCreating(true);
      setError(null);

      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        parent_id: createParent?.id || null,
        accountId,
        userId: user?.userId || user?.id
      });

      if (response.status === 201) {
        // Refresh the folders list
        await fetchFolders();

        // Close create row and reset form
        setShowCreateRow(false);
        setNewFolderName('');
        setCreateParent(null);
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create folder';
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateRow(false);
    setNewFolderName('');
    setCreateParent(null);
    setError(null);
  };

  // Open the inline create row with a parent folder preselected
  const handleAddSubfolder = (folder) => {
    setCreateParent(folder);
    setNewFolderName('');
    setShowCreateRow(true);
    setOpenDropdownId(null);
    setError(null);
  };

  // Handle edit folder
  const handleEditFolder = (folder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
    setError(null);
  };

  const handleUpdateFolder = async () => {
    if (!editingFolderName.trim()) return;

    try {
      setIsUpdating(true);
      setError(null);

      const response = await api.put(getApiUrl(`/api/v2/folders/${editingFolderId}`), {
        name: editingFolderName.trim()
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        // Update the folder in local state; sub-folders on this page show the
        // parent's name as a prefix, so refresh that too when a parent renames
        setFolders(prev => prev.map(f =>
          f.id === editingFolderId
            ? { ...f, name: editingFolderName.trim() }
            : f.parent_id === editingFolderId
              ? { ...f, parent_name: editingFolderName.trim() }
              : f
        ));

        // Exit edit mode
        setEditingFolderId(null);
        setEditingFolderName('');
      }
    } catch (err) {
      console.error('Error updating folder:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update folder';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingFolderId(null);
    setEditingFolderName('');
    setError(null);
  };

  // Open the move-folder modal and load eligible targets (top-level folders)
  const handleOpenMoveFolder = async (folder) => {
    setMovingFolder(folder);
    setSelectedMoveTarget(null);
    setMoveError(null);
    setOpenDropdownId(null);
    setMoveTargetsLoading(true);

    try {
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          sortBy: 'name',
          sortOrder: 'asc',
          page: 1,
          limit: 100
        }
      });

      const allFolders = response.data.folders || response.data || [];
      // Only top-level folders can be parents; exclude the folder itself and
      // its current parent (a no-op move)
      setMoveTargets(allFolders.filter(f =>
        !f.parent_id && f.id !== folder.id && f.id !== folder.parent_id
      ));
    } catch (err) {
      console.error('Error loading folders for move:', err);
      setMoveError('Failed to load folders');
      setMoveTargets([]);
    } finally {
      setMoveTargetsLoading(false);
    }
  };

  const handleConfirmMoveFolder = async () => {
    if (!movingFolder || !selectedMoveTarget) return;

    try {
      setIsMovingFolder(true);
      setMoveError(null);

      const response = await api.patch(getApiUrl(`/api/v2/folders/${movingFolder.id}`), {
        parent_id: selectedMoveTarget === 'top' ? null : selectedMoveTarget.id
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        await fetchFolders();
        setMovingFolder(null);
        setSelectedMoveTarget(null);
      }
    } catch (err) {
      console.error('Error moving folder:', err);
      setMoveError(err.response?.data?.error || 'Failed to move folder');
    } finally {
      setIsMovingFolder(false);
    }
  };

  const handleCancelMoveFolder = () => {
    if (isMovingFolder) return;
    setMovingFolder(null);
    setSelectedMoveTarget(null);
    setMoveError(null);
  };

  // Create row component for adding new folders
  const createRow = showCreateRow ? (
    <>
      <div className="data-table-cell folder-name">
        {createParent && (
          <span className="subfolder-parent-label" title={`New sub-folder of "${createParent.name}"`}>
            {createParent.name} /
          </span>
        )}
        <input
          type="text"
          placeholder={createParent ? 'Enter sub-folder name...' : 'Enter folder name...'}
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCreateFolder();
            } else if (e.key === 'Escape') {
              handleCancelCreate();
            }
          }}
          className="create-folder-input"
          autoFocus
          disabled={isCreating}
        />
      </div>
      <div className="data-table-cell campaign-count">-</div>
      <div className="data-table-cell last-updated">-</div>
      <div className="data-table-cell actions-cell">
        <div className="create-folder-actions">
          <button
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || isCreating}
            className="create-action-btn save-btn"
            title="Save folder"
          >
            {isCreating ? '⟳' : '✓'}
          </button>
          <button
            onClick={handleCancelCreate}
            disabled={isCreating}
            className="create-action-btn cancel-btn"
            title="Cancel"
          >
            ✕
          </button>
        </div>
      </div>
    </>
  ) : null;

  // Backend handles filtering, sorting, and pagination

  // Format date helper with time
  const formatDate = (folder) => {
    const date = folder.updated_at || folder.created_at;
    if (!date) return 'Never';

    const now = new Date();
    const folderDate = new Date(date);
    const diffTime = Math.abs(now - folderDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Format time (e.g., "8:35a", "10:22p")
    const timeString = folderDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase().replace(' ', '');

    if (diffDays === 1) return `Today ${timeString}`;
    if (diffDays === 2) return `Yesterday ${timeString}`;
    if (diffDays <= 7) return `${diffDays - 1} days ago`;

    return folderDate.toLocaleDateString();
  };

  // Handle dropdown toggle (following sidebar pattern)
  const toggleDropdown = (folderId, e) => {
    e.stopPropagation();

    // If clicking on the same dropdown that's already open, close it
    if (openDropdownId === folderId) {
      setOpenDropdownId(null);
      return;
    }

    // Otherwise, open the new dropdown
    const button = e.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    // Calculate if dropdown should appear above or below based on available
    // space. Item count varies: Pin + Delete always; with sub-folders enabled
    // every row gains "Move to..." and top-level rows also gain "Add Sub-folder".
    const rowFolder = folders.find(f => f.id === folderId);
    const itemCount = subfoldersEnabled ? (rowFolder && !rowFolder.parent_id ? 4 : 3) : 2;
    const dropdownHeight = itemCount * 50;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spacing = 8; // Spacing for visual appeal

    // Set position based on available space
    let position;
    if (spaceBelow < dropdownHeight + spacing && buttonRect.top > dropdownHeight + spacing) {
      position = 'top';
    } else {
      position = 'bottom';
    }

    // Set dropdown coordinates for fixed positioning
    const dropdownWidth = 160;
    setDropdownPosition({
      top: position === 'top' ? buttonRect.top - dropdownHeight - spacing : buttonRect.bottom + spacing,
      left: Math.max(10, buttonRect.right - dropdownWidth), // Ensure dropdown doesn't go off-screen
      position: position
    });

    setOpenDropdownId(folderId);
  };

  // Close dropdown when clicking outside (following sidebar pattern)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdownId &&
        !event.target.closest('.sidebar-dropdown-container') &&
        !event.target.closest('.sidebar-action-button') // Don't close if clicking on the action button
      ) {
        setOpenDropdownId(null);
      }
    };

    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDropdownId]);

  // Define table columns (after toggleDropdown is declared)
  const columns = [
    {
      key: 'name',
      label: 'Folder Name',
      width: '1fr',
      sortable: true,
      resizable: true,
      render: (folder) => (
        <div className="folder-name-content">
          {folder.is_pinned && (
            <FontAwesomeIcon
              icon={faThumbtack}
              className="pinned-icon"
              title="Pinned folder"
            />
          )}
          {editingFolderId === folder.id ? (
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editingFolderName}
                onChange={(e) => setEditingFolderName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUpdateFolder();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                className="inline-edit-input"
                autoFocus
                disabled={isUpdating}
              />
              <div className="inline-edit-actions">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUpdateFolder();
                  }}
                  disabled={!editingFolderName.trim() || isUpdating}
                  className="inline-edit-btn save-btn"
                  title="Save"
                >
                  {isUpdating ? '⟳' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  disabled={isUpdating}
                  className="inline-edit-btn cancel-btn"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <>
              {folder.parent_name && (
                <span
                  className="subfolder-parent-label"
                  title={`Sub-folder of "${folder.parent_name}"`}
                >
                  {folder.parent_name} /
                </span>
              )}
              <span
                className="folder-name-text editable-text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditFolder(folder);
                }}
                title="Click to edit"
              >
                {folder.name}
              </span>
            </>
          )}
        </div>
      )
    },
    {
      key: 'campaign_count',
      label: 'Campaigns',
      width: '200px',
      sortable: true,
      className: 'campaign-count',
      render: (folder) => {
        const count = folder.campaign_count || folder.campaignCount || 0;
        if (count > 0) {
          return (
            <button
              className="campaign-count-link"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/campaigns?folderId=${folder.id}`);
              }}
              title={`View ${count} campaign${count > 1 ? 's' : ''} in this folder`}
            >
              {count}
            </button>
          );
        }
        return <span className="campaign-count-zero">{count}</span>;
      }
    },
    {
      key: 'updated_at',
      label: 'Last Updated',
      width: '200px',
      sortable: true,
      className: 'last-updated',
      render: (folder) => formatDate(folder)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown)
  ];

  // Dropdown Menu Component using Portal (following sidebar pattern)
  const DropdownMenu = ({ folder, position }) => {
    if (!folder) return null;

    return createPortal(
      <div
        className={`sidebar-dropdown-container ${position.position === 'top' ? 'position-top' : ''}`}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left
        }}
      >
        {subfoldersEnabled && !folder.parent_id && (
          <button
            className="sidebar-dropdown-item"
            onClick={(e) => {
              e.stopPropagation();
              handleAddSubfolder(folder);
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FontAwesomeIcon icon={faFolderPlus} />
              Add Sub-folder
            </span>
          </button>
        )}
        {subfoldersEnabled && (
          <button
            className="sidebar-dropdown-item"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMoveFolder(folder);
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FontAwesomeIcon icon={faArrowRight} />
              Move to...
            </span>
          </button>
        )}
        <button
          className="sidebar-dropdown-item"
          onClick={(e) => {
            e.stopPropagation();
            handlePinFolder(folder);
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faThumbtack} />
            {folder.is_pinned ? 'Unpin' : 'Pin'}
          </span>
        </button>
        <button
          className="sidebar-dropdown-item"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteFolder(folder);
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faTrash} />
            Delete
          </span>
        </button>
      </div>,
      document.body
    );
  };

  if (!accountId) {
    return (
      <div className="page-container">
        <div className="page-error">
          <p>Please select an account to view folders.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>Folders <span className="count-badge">{pagination.totalItems || folders.length}</span></h1>
          <p>Organize your campaigns into folders</p>
        </div>
        <div className="header-right">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search folders..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={() => {
              setCreateParent(null);
              setShowCreateRow(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Folder
          </button>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
        </div>
      )}

      <DataTable
        data={folders}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faFolder} className="empty-state-icon" />
            <h3>No folders yet</h3>
            <p>Create your first folder to get started</p>
          </div>
        }
        createRow={createRow}
        className="folders-table"
        storageKey="folders"
      />



      {/* Render dropdown menu using portal */}
      {openDropdownId && (
        <DropdownMenu
          folder={folders.find(f => f.id === openDropdownId)}
          position={dropdownPosition}
        />
      )}

      {/* Move Folder Modal */}
      {movingFolder && (
        <div className="modal-overlay" onClick={handleCancelMoveFolder}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: '8px' }} />
                Move Folder
              </h2>
              <button className="close-button" onClick={handleCancelMoveFolder}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                <strong>{movingFolder.name}</strong>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {movingFolder.parent_name
                    ? `Currently in "${movingFolder.parent_name}"`
                    : 'Currently a top-level folder'}
                </div>
              </div>

              {moveError && (
                <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#FEF2F2', color: '#B91C1C', borderRadius: '6px', fontSize: '13px' }}>
                  {moveError}
                </div>
              )}

              <div className="form-group">
                <label>Move to</label>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white' }}>
                  {moveTargetsLoading && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading folders…</div>
                  )}
                  {!moveTargetsLoading && movingFolder.parent_id && (
                    <button
                      type="button"
                      onClick={() => setSelectedMoveTarget('top')}
                      style={{
                        width: '100%', padding: '12px 16px', border: 'none', textAlign: 'left',
                        background: selectedMoveTarget === 'top' ? '#EBF8FF' : 'transparent',
                        display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px'
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                      <span style={{ flex: 1 }}>Top level (no parent folder)</span>
                    </button>
                  )}
                  {!moveTargetsLoading && moveTargets.map(target => (
                    <button
                      key={target.id}
                      type="button"
                      onClick={() => setSelectedMoveTarget(target)}
                      style={{
                        width: '100%', padding: '12px 16px', border: 'none', textAlign: 'left',
                        background: selectedMoveTarget !== 'top' && selectedMoveTarget?.id === target.id ? '#EBF8FF' : 'transparent',
                        display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px'
                      }}
                    >
                      <FontAwesomeIcon icon={faFolder} />
                      <span style={{ flex: 1 }}>{target.name}</span>
                    </button>
                  ))}
                  {!moveTargetsLoading && moveTargets.length === 0 && !movingFolder.parent_id && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      No other top-level folders to move under
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelMoveFolder}
                disabled={isMovingFolder}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmMoveFolder}
                disabled={!selectedMoveTarget || isMovingFolder}
              >
                {isMovingFolder ? 'Moving...' : 'Move Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoldersPage;
